import { type ApiRequestOptions, apiJson } from "@/api/apiClient";

export interface ChatResponse {
	content: string;
	threadId?: string;
	jobId?: string;
	status?: string;
}

export interface SendChatMessageParams {
	message: string;
	documentIds?: string[];
	graphId?: string;
	workspaceId?: string;
	threadId?: string;
}

export interface ChatThread {
	id: string;
	title: string | null;
	ownerId: string;
	workspaceId: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface ChatMessage {
	id: string;
	threadId: string;
	role: "user" | "assistant";
	content: string;
	metadata: Record<string, unknown>;
	createdAt: string;
}

export interface ChatThreadWithMessages extends ChatThread {
	messages: ChatMessage[];
}

export interface CreateChatThreadParams {
	title?: string;
	workspaceId?: string | null;
}

/**
 * Send a message. Uses GraphQA (Classifier → Finder → Reasoner → Summarizer) when graphId is provided,
 * or ChatRouter (generate_graph / add_documents) when documentIds are selected.
 */
export const sendChatMessage = async (
	params: SendChatMessageParams,
	options?: ApiRequestOptions,
): Promise<ChatResponse> => {
	return apiJson<ChatResponse>("/chat", {
		method: "POST",
		body: JSON.stringify(params),
		signal: options?.signal,
	});
};

/**
 * List chat threads for the current user, optionally filtered by workspaceId.
 */
export const listChatThreads = async (
	params?: { workspaceId?: string | null },
	options?: ApiRequestOptions,
): Promise<ChatThread[]> => {
	const search = new URLSearchParams();
	if (params?.workspaceId) search.set("workspaceId", params.workspaceId);
	const queryString = search.toString();
	return apiJson<ChatThread[]>(`/chat/threads${queryString ? `?${queryString}` : ""}`, {
		method: "GET",
		signal: options?.signal,
	});
};

/**
 * Get a chat thread with messages
 */
export const getChatThread = async (threadId: string, options?: ApiRequestOptions): Promise<ChatThreadWithMessages> => {
	return apiJson<ChatThreadWithMessages>(`/chat/threads/${threadId}`, {
		method: "GET",
		signal: options?.signal,
	});
};

/**
 * Create a new chat thread
 */
export const createChatThread = async (
	params: CreateChatThreadParams,
	options?: ApiRequestOptions,
): Promise<ChatThread> => {
	return apiJson<ChatThread>("/chat/threads", {
		method: "POST",
		body: JSON.stringify(params),
		signal: options?.signal,
	});
};

/**
 * Update a chat thread
 */
export const updateChatThread = async (
	threadId: string,
	params: { title?: string | null },
	options?: ApiRequestOptions,
): Promise<ChatThread> => {
	return apiJson<ChatThread>(`/chat/threads/${threadId}`, {
		method: "PATCH",
		body: JSON.stringify(params),
		signal: options?.signal,
	});
};

/**
 * Delete a chat thread
 */
export const deleteChatThread = async (threadId: string, options?: ApiRequestOptions): Promise<void> => {
	return apiJson<void>(`/chat/threads/${threadId}`, {
		method: "DELETE",
		signal: options?.signal,
	});
};
